import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollWindow } from './Research';

interface AlpacaAccount {
  portfolio_value: number;
  cash: number;
  buying_power: number;
  equity: number;
  daily_pnl: number;
  daily_pnl_pct: number;
  position_count: number;
  positions: AlpacaPosition[];
}

interface AlpacaPosition {
  ticker: string;
  qty: number;
  avg_entry: number;
  current_price: number;
  market_value: number;
  unrealised_pnl: number;
  unrealised_pnl_pct: number;
  side: string;
  exchange?: string;
  pnl?: number;
  pnl_pct?: number;
}

interface ASXAccount {
  cash_balance: number;
  invested_value: number;
  total_value: number;
  total_pnl: number;
  total_pnl_pct: number;
}

interface ASXPosition {
  id: number;
  ticker: string;
  quantity: number;
  entry_price: number;
  current_price: number;
  market_value: number;
  unrealised_pnl: number;
  unrealised_pnl_pct: number;
}

interface Signal {
  id?: number;
  ticker: string;
  market: string;
  action: string;
  confidence: number;
  rationale: string;
  created_at: string;
  valuation_score?: number;
  quality_score?: number;
  momentum_score?: number;
  sentiment_score?: number;
  macro_score?: number;
  final_score?: number;
  ml_signal?: string;
  ml_confidence?: number;
  rules_score?: number;
  breakdown?: Record<string, any>;
}

interface PositionSignal {
  action: string;
  confidence: number | null;
  final_score: number | null;
  created_at: string | null;
}

interface MarketStatus {
  exchange: string;
  is_open: boolean;
  local_time: string;
  local_tz: string;
  open_time: string;
  close_time: string;
  local_day: string;
}

export interface WorkerInfo {
  id: string;
  name: string;
  vm: string;
  host: string;
  port?: number;
  status: 'online' | 'offline' | 'error';
  worker_status?: string;
  latency_ms?: number;
  timestamp?: string;
  error?: string;
}

interface DbPosition {
  position_id: string;
  ticker: string;
  exchange: string;
  market: string;
  qty_held: number;
  avg_cost: number;
  current_price: number;
  market_value: number;
  unrealised_pnl: number;
  realised_pnl: number;
  trade_count: number;
  first_bought_at: string | null;
  last_updated: string | null;
  is_held: boolean;
  hold_reason: string | null;
  hold_until: string | null;
  is_permanent: boolean;
  signal_score?: number | null;
}

interface TradeRecord {
  id: number | string;
  position_id: string;
  ticker: string;
  exchange: string;
  market: string;
  action: string;
  quantity: number;
  price: number | null;
  total_value: number | null;
  avg_cost_at_trade: number | null;
  pnl: number | null;
  duration_hours: number | null;
  signal_score: number | null;
  buy_signal_score: number | null;
  notes: string | null;
  status: string;
  executed_at: string | null;
}

interface OpenOrder {
  order_id: string;
  broker_order_id: string | null;
  ticker: string;
  exchange: string;
  action: string;
  quantity: number;
  status: string;
  created_at: string | null;
  hold_until: string | null;
}

const API = '/api';

function api(path: string, token: string) {
  return fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
}

// ─── Portfolio Cache (30s TTL) ─────────────────────────────────────────────

interface PortfolioCacheEntry {
  summary: { asx_cash: number; asx_equity: number; asx_positions_value: number } | null;
  alpacaAccount: AlpacaAccount | null;
  alpacaPositions: AlpacaPosition[];
  asxAccount: ASXAccount | null;
  asxPositions: ASXPosition[];
  positionSignals: Record<string, PositionSignal>;
  fetchedAt: number;
}
let _portfolioCache: PortfolioCacheEntry | null = null;
const CACHE_TTL = 30_000;

export async function prefetchPortfolioData(token: string): Promise<void> {
  if (_portfolioCache && Date.now() - _portfolioCache.fetchedAt < CACHE_TTL) return;
  try {
    const [summaryRes, asxAccRes, asxPosRes] = await Promise.all([
      api('/portfolio/summary', token),
      api('/asx-paper/account', token),
      api('/asx-paper/positions', token),
    ]);
    const entry: PortfolioCacheEntry = {
      summary: null,
      alpacaAccount: null, alpacaPositions: [],
      asxAccount: null, asxPositions: [],
      positionSignals: {},
      fetchedAt: Date.now(),
    };
    if (summaryRes.ok) {
      const d = await summaryRes.json();
      entry.alpacaAccount = {
        portfolio_value: d.portfolio_value ?? 0,
        cash: d.cash ?? 0,
        buying_power: d.buying_power ?? 0,
        equity: d.portfolio_value ?? 0,
        daily_pnl: d.day_pnl ?? 0,
        daily_pnl_pct: d.day_pnl_pct ?? 0,
        position_count: 0,
        positions: [],
      };
      entry.summary = {
        asx_cash: d.asx_cash ?? 0,
        asx_equity: d.asx_equity ?? 0,
        asx_positions_value: d.asx_positions_value ?? 0,
      };
    }
    if (asxAccRes.ok) { entry.asxAccount = await asxAccRes.json(); }
    if (asxPosRes.ok) { const d = await asxPosRes.json(); entry.asxPositions = d.positions || []; }
    _portfolioCache = entry;
  } catch {}
}

function SkeletonCard() {
  return (
    <div className="bg-surface rounded-card p-4 border border-border-base animate-pulse">
      <div className="h-2.5 bg-surface-2 rounded w-20 mb-3"></div>
      <div className="h-6 bg-surface-2 rounded w-28 mb-2"></div>
      <div className="h-2 bg-surface-2 rounded w-16"></div>
    </div>
  );
}

function fmt(value: number | null | undefined, decimals = 2): string {
  if (!value && value !== 0) return 'N/A';
  return value.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function PnLValue({ value, pct, showPct = true, suffix }: { value: number; pct?: number; showPct?: boolean; suffix?: string }) {
  const isPositive = value >= 0;
  const color = isPositive ? 'text-green-400' : 'text-red-400';
  return (
    <span className={color}>
      {isPositive ? '+' : '-'}${fmt(Math.abs(value))}
      {showPct && pct !== undefined && ` (${isPositive ? '+' : ''}${pct.toFixed(2)}%)`}
      {suffix && ` ${suffix}`}
    </span>
  );
}

function toAEST(isoStr: string): string {
  try {
    return new Date(isoStr).toLocaleString('en-AU', {
      timeZone: 'Australia/Brisbane',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }) + ' AEST';
  } catch { return isoStr; }
}

interface SummaryCardProps {
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  sub?: React.ReactNode;
}

function SummaryCard({ label, value, valueColor = 'text-text-bright', sub = '' }: SummaryCardProps) {
  return (
    <div className="bg-surface rounded-card p-4 border border-border-base min-w-0">
      <p className="text-text-muted text-xs font-semibold uppercase tracking-wide truncate">{label}</p>
      <p className={`text-xl font-bold mt-2 ${valueColor}`}>{value}</p>
      <div className="text-text-muted text-xs mt-1 truncate">{sub}</div>
    </div>
  );
}

// ─── FIX 4: Smart VM Status Pill ──────────────────────────────────────────────

export type BotStatus = 'running' | 'degraded' | 'alert';

export function getVMStatus(master: WorkerInfo | null, workers: WorkerInfo[]): BotStatus {
  const all = master ? [master, ...workers] : workers;
  if (all.length === 0) return 'alert';
  const anyDown = all.some(v => v.status === 'offline');
  if (anyDown) return 'alert';
  const anyDegraded = all.some(v => v.status === 'error');
  if (anyDegraded) return 'degraded';
  return 'running';
}

// ─── FIX 5: Market Status with AEST ──────────────────────────────────────────

function localToAEST(localTime: string, localDay: string, localTz: string): string {
  // Convert local exchange time to UTC then add exactly 10 hours for AEST
  try {
    const now = new Date();
    const todayISO = now.toISOString().split('T')[0];
    const [h, m] = localTime.split(':').map(Number);
    // We need to convert the local exchange time to AEST (UTC+10)
    // Strategy: get UTC offset of exchange timezone, then add 10h for AEST
    const tzOffsets: Record<string, number> = {
      'America/New_York': -5, // EST (we hardcode standard time for display)
      'Australia/Sydney': 10,
      'Europe/London': 0,
      'Asia/Hong_Kong': 8,
      'Europe/Berlin': 1,
      'Europe/Stockholm': 1,
    };
    // Use the actual UTC offset by checking the exchange tz
    // Create a date at the exchange's current time
    const exchangeDate = new Date(`${todayISO}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
    // Get the UTC offset for this timezone at this moment
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: localTz, timeZoneName: 'shortOffset' });
    const parts = formatter.formatToParts(exchangeDate);
    const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const match = offsetPart.match(/GMT([+-]\d+(?::\d+)?)/);
    let offsetHours = 0;
    if (match) {
      const parts2 = match[1].split(':');
      offsetHours = parseInt(parts2[0]);
      if (parts2[1]) offsetHours += parseInt(parts2[1]) / 60 * Math.sign(offsetHours);
    }
    // UTC time = local - offset, AEST = UTC + 10
    const aestMinutes = h * 60 + m - offsetHours * 60 + 10 * 60;
    const aestH = Math.floor(((aestMinutes % 1440) + 1440) % 1440 / 60);
    const aestM = ((aestMinutes % 60) + 60) % 60;
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const exchangeDayIdx = dayNames.indexOf(localDay.substring(0, 3));
    let aestDayIdx = exchangeDayIdx;
    if (aestMinutes >= 1440) aestDayIdx = (exchangeDayIdx + 1) % 7;
    else if (aestMinutes < 0) aestDayIdx = (exchangeDayIdx + 6) % 7;
    return `${dayNames[aestDayIdx] || localDay.substring(0, 3)} ${String(aestH).padStart(2, '0')}:${String(aestM).padStart(2, '0')}`;
  } catch {
    return '—';
  }
}

function openCloseToAEST(openTime: string, closeTime: string, localTz: string): string {
  // Convert open/close times to AEST
  const convertOne = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const now = new Date();
    const todayISO = now.toISOString().split('T')[0];
    try {
      const formatter = new Intl.DateTimeFormat('en-US', { timeZone: localTz, timeZoneName: 'shortOffset' });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';
      const match = offsetPart.match(/GMT([+-]\d+(?::\d+)?)/);
      let offsetHours = 0;
      if (match) {
        const ps = match[1].split(':');
        offsetHours = parseInt(ps[0]);
      }
      const aestMinutes = h * 60 + m - offsetHours * 60 + 10 * 60;
      const aestH = Math.floor(((aestMinutes % 1440) + 1440) % 1440 / 60);
      const aestM = ((aestMinutes % 60) + 60) % 60;
      return `${String(aestH).padStart(2, '0')}:${String(aestM).padStart(2, '0')}`;
    } catch { return t; }
  };
  return `${convertOne(openTime)}–${convertOne(closeTime)}`;
}

function MarketStatusRow({ status }: { status: MarketStatus | null }) {
  if (!status) return null;
  const tzCity = status.local_tz.split('/')[1]?.replace('_', ' ') || status.local_tz;
  const aestCurrentTime = localToAEST(status.local_time, status.local_day, status.local_tz);
  const aestHoursRange = openCloseToAEST(status.open_time, status.close_time, status.local_tz);
  const localHours = `${status.open_time}–${status.close_time}`;

  return (
    <div className="px-4 py-2 border-b border-border-base flex items-center gap-3 flex-wrap">
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
        status.is_open ? 'bg-status-green-soft text-status-green-muted border border-border-green' : 'bg-status-red-soft text-status-red border border-border-red'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${status.is_open ? 'bg-status-green-btn animate-pulse' : 'bg-status-red'}`} />
        {status.is_open ? 'OPEN' : 'CLOSED'}
      </span>
      <span className="text-xs text-text-secondary">
        ({status.is_open ? 'Open' : 'Closed'}) {status.local_day.substring(0, 3)} {status.local_time} · Mon-Fri {localHours} {tzCity} | {aestCurrentTime} · Mon-Fri {aestHoursRange} AEST
      </span>
    </div>
  );
}

// ─── FIX 6: Expandable Signal Cards ──────────────────────────────────────────

function formatBreakdownPlainEnglish(breakdown: Record<string, any>, signal: Signal): React.ReactNode {
  if (!breakdown || Object.keys(breakdown).length === 0) {
    return <p className="text-text-muted text-xs italic">Detailed breakdown not available for this signal</p>;
  }

  const action = signal.action || 'HOLD';
  const score = signal.final_score ?? signal.confidence ?? 0;
  const actionColor = action === 'BUY' ? 'text-status-green' : action === 'SELL' ? 'text-status-red' : 'text-status-amber';

  const rows: React.ReactNode[] = [];

  // Valuation
  const val = signal.valuation_score ?? breakdown.valuation_score;
  const pe = breakdown.pe_ratio ?? breakdown.pe;
  const pb = breakdown.pb_ratio ?? breakdown.pb;
  if (val !== undefined) {
    let text = `Score ${val?.toFixed(1) ?? '?'}/10.`;
    if (pe) text += ` P/E ratio: ${parseFloat(pe).toFixed(1)}.`;
    if (pb) text += ` P/B ratio: ${parseFloat(pb).toFixed(1)}.`;
    rows.push(<li key="val"><span className="text-text-primary">Valuation ({val?.toFixed(1)}/10):</span> <span className="text-text-secondary">{text}</span></li>);
  }

  // Quality
  const qual = signal.quality_score ?? breakdown.quality_score;
  const roe = breakdown.roe;
  const de = breakdown.debt_to_equity ?? breakdown.de_ratio;
  if (qual !== undefined) {
    let text = `Score ${qual?.toFixed(1) ?? '?'}/10.`;
    if (roe) text += ` ROE: ${parseFloat(roe).toFixed(1)}%.`;
    if (de) text += ` D/E ratio: ${parseFloat(de).toFixed(2)}.`;
    rows.push(<li key="qual"><span className="text-text-primary">Quality ({qual?.toFixed(1)}/10):</span> <span className="text-text-secondary">{text}</span></li>);
  }

  // Momentum
  const mom = signal.momentum_score ?? breakdown.momentum_score;
  const ret1d = breakdown.return_1d;
  const ret5d = breakdown.return_5d;
  const ret20d = breakdown.return_20d;
  if (mom !== undefined) {
    let text = `Score ${mom?.toFixed(1) ?? '?'}/10.`;
    if (ret1d != null) text += ` 1d: ${(parseFloat(ret1d) * 100).toFixed(1)}%.`;
    if (ret5d != null) text += ` 5d: ${(parseFloat(ret5d) * 100).toFixed(1)}%.`;
    if (ret20d != null) text += ` 20d: ${(parseFloat(ret20d) * 100).toFixed(1)}%.`;
    rows.push(<li key="mom"><span className="text-text-primary">Momentum ({mom?.toFixed(1)}/10):</span> <span className="text-text-secondary">{text}</span></li>);
  }

  // Sentiment
  const sent = signal.sentiment_score ?? breakdown.sentiment_score;
  if (sent !== undefined) {
    const sentText = sent >= 7 ? 'Very positive news sentiment.' : sent >= 5 ? 'Neutral to mixed sentiment.' : 'Negative news sentiment.';
    rows.push(<li key="sent"><span className="text-text-primary">Sentiment ({sent?.toFixed(1)}/10):</span> <span className="text-text-secondary">{sentText}</span></li>);
  }

  // Macro
  const macro = signal.macro_score ?? breakdown.macro_score;
  if (macro !== undefined) {
    const macroText = macro >= 7 ? 'Favourable macro conditions.' : macro >= 5 ? 'Neutral macro environment.' : 'Challenging macro conditions.';
    rows.push(<li key="macro"><span className="text-text-primary">Macro ({macro?.toFixed(1)}/10):</span> <span className="text-text-secondary">{macroText}</span></li>);
  }

  // ML
  const ml = signal.ml_signal;
  const mlConf = signal.ml_confidence;
  if (ml) {
    rows.push(<li key="ml"><span className="text-text-primary">ML Model:</span> <span className="text-text-secondary">XGBoost signal: {ml}{mlConf != null ? `, confidence ${(mlConf * 100).toFixed(0)}%` : ''}</span></li>);
  }

  // Final
  const rulesScore = signal.rules_score;
  if (rulesScore != null) {
    rows.push(<li key="final"><span className="text-text-primary">Final:</span> <span className="text-text-secondary">Rules 60% + ML 40% = {score.toFixed(1)}/10</span></li>);
  }

  return (
    <div className="mt-3 pt-3 border-t border-border-base">
      <p className="text-xs font-semibold mb-2">
        Score: {score.toFixed(1)}/10 — <span className={actionColor}>{action}</span> signal
      </p>
      <ul className="space-y-1 text-xs list-none">
        {rows.length > 0 ? rows : <li className="text-text-muted italic">No detailed breakdown available</li>}
      </ul>
    </div>
  );
}

// ─── P&L Overview Tile — closed trades (4 periods) + unrealised, per currency ──

function sydneyDateStr(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
}

function sydneyShortDate(dateStr: string): string {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-AU', { timeZone: 'UTC', day: '2-digit', month: 'short' });
}

interface PnlPeriodBounds {
  label: string;
  sinceStr: string;
}

function getPnlPeriodBounds(): PnlPeriodBounds[] {
  const now = new Date();
  const todayStr = sydneyDateStr(now);
  const [ty, tm, td] = todayStr.split('-').map(Number);
  const anchorUTCNoon = new Date(Date.UTC(ty, tm - 1, td, 12));

  const weekdayShort = now.toLocaleDateString('en-US', { timeZone: 'Australia/Sydney', weekday: 'short' });
  const weekdayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(weekdayShort);
  const mondayAnchor = new Date(anchorUTCNoon);
  mondayAnchor.setUTCDate(mondayAnchor.getUTCDate() - Math.max(weekdayIndex, 0));

  const monthStartStr = `${ty}-${String(tm).padStart(2, '0')}-01`;
  const fyStartYear = tm >= 7 ? ty : ty - 1;
  const fyStartStr = `${fyStartYear}-07-01`;

  return [
    { label: 'Daily', sinceStr: todayStr },
    { label: 'Week to Date', sinceStr: sydneyDateStr(mondayAnchor) },
    { label: 'Month to Date', sinceStr: monthStartStr },
    { label: 'Year to Date (FY)', sinceStr: fyStartStr },
  ];
}

function sumClosedPnl(trades: TradeRecord[], market: string, sinceStr: string): number {
  return trades
    .filter(t => t.market === market && t.pnl != null && t.executed_at && sydneyDateStr(new Date(t.executed_at)) >= sinceStr)
    .reduce((sum, t) => sum + (t.pnl || 0), 0);
}

// Failed manual-order attempts are merged into /trades as pnl-less rows going back months —
// excluding status='failed' keeps this to genuine trade history when checking for reset gaps.
function earliestTradeDateStr(trades: TradeRecord[], market: string): string | null {
  const dates = trades
    .filter(t => t.market === market && t.status !== 'failed' && t.executed_at)
    .map(t => sydneyDateStr(new Date(t.executed_at as string)));
  if (dates.length === 0) return null;
  return dates.reduce((min, d) => (d < min ? d : min));
}

function PnlOverviewTile({ token, asxUnrealisedPnl, usUnrealisedPnl }: { token: string; asxUnrealisedPnl: number; usUnrealisedPnl: number }) {
  const [trades, setTrades] = useState<TradeRecord[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await api('/trades?limit=1000', token);
      if (res.ok) {
        const d = await res.json();
        setTrades(d.trades || []);
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const periods = getPnlPeriodBounds();
  const earliestAsx = earliestTradeDateStr(trades, 'ASX');
  const earliestUs = earliestTradeDateStr(trades, 'US');

  const renderStack = (asxVal: number, usVal: number, asxThin: boolean, usThin: boolean) => (
    <span className="flex flex-col gap-0 text-base leading-snug font-bold">
      <span className={asxVal >= 0 ? 'text-status-green' : 'text-status-red'}>
        {asxVal >= 0 ? '+' : '-'}${fmt(Math.abs(asxVal), 0)} <span className="text-xs font-normal text-text-secondary">AUD</span>
      </span>
      {asxThin && <span className="text-text-muted text-xs font-normal block">since reset ({sydneyShortDate(earliestAsx as string)})</span>}
      <span className={usVal >= 0 ? 'text-status-green' : 'text-status-red'}>
        {usVal >= 0 ? '+' : '-'}${fmt(Math.abs(usVal), 0)} <span className="text-xs font-normal text-text-secondary">USD</span>
      </span>
      {usThin && <span className="text-text-muted text-xs font-normal block">since reset ({sydneyShortDate(earliestUs as string)})</span>}
    </span>
  );

  return (
    <div className="bg-surface rounded-card border border-border-base">
      <div className="px-4 py-3 border-b border-border-base">
        <h2 className="font-semibold text-sm text-text-primary">P&L Overview</h2>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {periods.map(({ label, sinceStr }) => {
          const asxPnl = sumClosedPnl(trades, 'ASX', sinceStr);
          const usPnl = sumClosedPnl(trades, 'US', sinceStr);
          const asxThin = !!earliestAsx && earliestAsx > sinceStr;
          const usThin = !!earliestUs && earliestUs > sinceStr;
          return (
            <SummaryCard
              key={label}
              label={label}
              value={renderStack(asxPnl, usPnl, asxThin, usThin)}
              sub="Closed trades"
            />
          );
        })}
        <SummaryCard
          label="Unrealised P&L"
          value={renderStack(asxUnrealisedPnl, usUnrealisedPnl, false, false)}
          sub="From open positions"
        />
      </div>
    </div>
  );
}

// ─── Open Orders Panel ────────────────────────────────────────────────────────

function _relativeTime(ts: string | null): string {
  if (!ts) return '—';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

interface ActiveHold {
  position_id: string;
  ticker: string;
  exchange: string;
  hold_reason: string | null;
  hold_until: string | null;
  is_permanent: boolean;
  created_at: string | null;
}

function OpenOrdersPanel({
  openOrders,
  token,
  onRefresh,
  activeHolds,
  onCancelComplete,
}: {
  openOrders: { asx: OpenOrder[]; us: OpenOrder[] };
  token: string;
  onRefresh: () => void;
  activeHolds: ActiveHold[];
  onCancelComplete?: () => void;
}) {
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [removingHold, setRemovingHold] = useState<string | null>(null);
  const [cancelModalOrder, setCancelModalOrder] = useState<OpenOrder | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const allOrders = [...openOrders.asx, ...openOrders.us].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  const allHolds = [...activeHolds].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  const total = allOrders.length + allHolds.length;

  const cancelOrder = async (orderId: string, reason: string) => {
    setCancellingId(orderId);
    try {
      const numId = parseInt(orderId, 10);
      if (!isNaN(numId)) {
        const res = await fetch(`${API}/admin/manual-orders/${numId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ cancel_reason: reason }),
        });
        const d = await res.json();
        if (!res.ok) alert(d.detail || d.message || 'Failed to cancel');
      } else {
        alert('Cannot cancel broker-submitted orders from here — cancel via your broker interface.');
      }
      onRefresh();
      onCancelComplete?.();
    } catch (e: any) {
      alert(e.message);
    }
    setCancellingId(null);
  };

  const openCancelModal = (order: OpenOrder) => {
    setCancelModalOrder(order);
    setCancelReason('');
  };

  const confirmCancel = async () => {
    if (!cancelModalOrder) return;
    setCancelModalOrder(null);
    await cancelOrder(cancelModalOrder.order_id, cancelReason);
    setCancelReason('');
  };

  const removeHold = async (positionId: string) => {
    if (!window.confirm('Remove hold on this position?')) return;
    setRemovingHold(positionId);
    try {
      const res = await fetch(`${API}/admin/holds/${encodeURIComponent(positionId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json();
        alert(d.detail || d.message || 'Failed to remove hold');
      }
      onRefresh();
    } catch (e: any) {
      alert(e.message);
    }
    setRemovingHold(null);
  };

  return (
    <div>
      <div className="p-4 border-b border-border-base flex items-center gap-2">
        <h2 className="font-semibold text-sm">Open Orders</h2>
        <span className="bg-surface-2 text-text-primary text-xs px-2 py-0.5 rounded-full">{total}</span>
      </div>
      {total === 0 ? (
        <div className="p-4 text-text-muted text-sm">No open orders</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-base text-text-secondary">
                <th className="text-left px-4 py-2">Ticker</th>
                <th className="text-left px-4 py-2">Exchange</th>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-right px-4 py-2">Quantity</th>
                <th className="text-left px-4 py-2">Status / Reason</th>
                <th className="text-right px-4 py-2">Time</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-base">
              {allOrders.map((o, i) => (
                <tr key={`order-${i}`} className="hover:bg-surface-hover">
                  <td className="px-4 py-2 font-mono font-medium text-text-bright">{o.ticker}</td>
                  <td className="px-4 py-2">
                    <span className="bg-surface-2 text-text-primary px-1.5 py-0.5 rounded text-xs">{o.exchange}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      o.action === 'BUY' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-red-soft text-status-red-muted'
                    }`}>{o.action}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-text-primary">{o.quantity}</td>
                  <td className="px-4 py-2">
                    <span className="bg-status-amber-soft text-status-amber-muted px-2 py-0.5 rounded text-xs">
                      {o.status.charAt(0).toUpperCase() + o.status.slice(1)}
                    </span>
                    {o.hold_until && (
                      <div className="text-text-muted text-xs mt-0.5">
                        Hold until {toAEST(o.hold_until)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-text-secondary">{_relativeTime(o.created_at)}</td>
                  <td className="px-4 py-2 text-right">
                    {(o.status === 'queued' || o.status === 'pending') && (
                      <button
                        onClick={() => openCancelModal(o)}
                        disabled={cancellingId === o.order_id}
                        className="text-text-muted hover:text-status-red disabled:opacity-50 px-1"
                        title="Cancel order"
                      >✕</button>
                    )}
                  </td>
                </tr>
              ))}
              {allHolds.map((h, i) => (
                <tr key={`hold-${i}`} className="hover:bg-surface-hover bg-status-amber-soft/20">
                  <td className="px-4 py-2 font-mono font-medium text-text-bright">{h.ticker}</td>
                  <td className="px-4 py-2">
                    <span className="bg-surface-2 text-text-primary px-1.5 py-0.5 rounded text-xs">{h.exchange}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-status-amber-soft text-status-amber-muted">HOLD</span>
                  </td>
                  <td className="px-4 py-2 text-right text-text-muted">—</td>
                  <td className="px-4 py-2 text-text-secondary text-xs">{h.hold_reason || (h.is_permanent ? 'Permanent' : '—')}</td>
                  <td className="px-4 py-2 text-right text-text-secondary">{_relativeTime(h.created_at)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => removeHold(h.position_id)}
                      disabled={removingHold === h.position_id}
                      className="text-text-muted hover:text-status-red disabled:opacity-50 text-xs px-1"
                      title="Remove hold"
                    >✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cancelModalOrder && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setCancelModalOrder(null)}>
          <div className="bg-surface border border-border-light rounded-lg p-5 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-sm mb-3">Cancel Order</h3>
            <div className="text-xs text-text-secondary mb-3">
              {cancelModalOrder.action} {cancelModalOrder.quantity} × {cancelModalOrder.ticker} ({cancelModalOrder.exchange})
            </div>
            <label className="block text-xs text-text-secondary mb-1">Reason (optional)</label>
            <input
              type="text"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="e.g. Changed strategy"
              className="w-full bg-surface-2 border border-border-light rounded px-2 py-1.5 text-xs text-text-bright mb-4 focus:outline-none focus:border-border-blue"
              onKeyDown={e => { if (e.key === 'Enter') confirmCancel(); if (e.key === 'Escape') setCancelModalOrder(null); }}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCancelModalOrder(null)} className="px-3 py-1.5 text-xs bg-surface-2 hover:bg-surface-hover rounded">Keep Order</button>
              <button onClick={confirmCancel} className="px-3 py-1.5 text-xs bg-status-red-btn-hover hover:bg-status-red-btn rounded text-text-bright">Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Manual Override Panel ────────────────────────────────────────────────────

interface TickerInfo {
  ticker: string;
  exchange: string;
}

interface PositionData {
  ticker: string;
  exchange: string;
  qty_held: number;
  qty_pending_sell: number;
  qty_pending_buy: number;
  current_price: number | null;
  cash_available: number;
}

function ManualOverridePanel({ token, positions }: { token: string; positions: Array<{ ticker: string; exchange: string }> }) {
  const [ticker, setTicker] = useState('');
  const [exchange, setExchange] = useState('');
  const [exchangeLocked, setExchangeLocked] = useState(false);
  const [exchangeOptions, setExchangeOptions] = useState<string[]>([]);
  const [action, setAction] = useState('BUY');
  const [quantity, setQuantity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string; status?: string; estimated?: string } | null>(null);
  const [allTickers, setAllTickers] = useState<TickerInfo[]>([]);
  const [heldPositions, setHeldPositions] = useState<TickerInfo[]>([]);
  const [positionsWarning, setPositionsWarning] = useState(false);
  const [positionData, setPositionData] = useState<PositionData | null>(null);
  const [positionLoading, setPositionLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillApplied = useRef(false);
  const tickerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [holdReason, setHoldReason] = useState('');
  const [holdUntil, setHoldUntil] = useState('');
  const [isPermanent, setIsPermanent] = useState(false);
  const [holdsMap, setHoldsMap] = useState<Record<string, { hold_reason: string; hold_until: string | null; is_permanent: boolean }>>({});
  const [sellHoldWarning, setSellHoldWarning] = useState<{ holdInfo: { hold_reason: string; hold_until: string | null; is_permanent: boolean }; qty: number } | null>(null);
  const [pennyStockWarning, setPennyStockWarning] = useState<{ price: number; ticker: string; action: string; qty: number } | null>(null);
  const [botMode, setBotMode] = useState<string>('active');

  // Exchange display → canonical backend value
  const exchangeCanonical: Record<string, string> = {
    'ASX': 'ASX', 'CHI-X': 'CHI-X', 'NASDAQ': 'NASDAQ',
    'NYSE': 'NYSE', 'AMEX': 'AMEX', 'NYSE ARCA': 'NYSE ARCA', 'US': 'US',
  };

  // Load all active tickers on mount
  useEffect(() => {
    fetch(`${API}/admin/tickers/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setAllTickers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [token]);

  // Load held positions (for SELL filtering) — sources from DB positions table
  const loadHeldPositions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/portfolio/positions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setHeldPositions((d.positions || []).map((p: any) => ({ ticker: p.ticker, exchange: p.exchange })));
        setPositionsWarning(false);
      } else {
        setPositionsWarning(true);
      }
    } catch {
      setPositionsWarning(true);
    }
    // Also refresh holds map
    try {
      const hRes = await fetch(`${API}/admin/holds`, { headers: { Authorization: `Bearer ${token}` } });
      if (hRes.ok) {
        const hd = await hRes.json();
        const map: Record<string, { hold_reason: string; hold_until: string | null; is_permanent: boolean }> = {};
        for (const h of (hd.holds || [])) {
          map[`${h.ticker}:${h.exchange}`] = { hold_reason: h.hold_reason, hold_until: h.hold_until, is_permanent: h.is_permanent };
        }
        setHoldsMap(map);
      }
    } catch {}
  }, [token]);

  useEffect(() => { loadHeldPositions(); }, [loadHeldPositions]);

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch(`${API}/admin/kill-switch/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) { const d = await res.json(); setBotMode(d.mode || 'active'); }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [token]);

  // Pre-fill form from URL params: ?override=TICKER&exchange=EXCHANGE
  useEffect(() => {
    if (prefillApplied.current) return;
    const overrideTicker = searchParams.get('override');
    const overrideExchange = searchParams.get('exchange');
    if (overrideTicker) {
      prefillApplied.current = true;
      setTicker(overrideTicker.toUpperCase());
      if (overrideExchange) {
        setExchange(overrideExchange);
        setExchangeLocked(true);
        setExchangeOptions([overrideExchange]);
      }
      // Clear params from URL without navigation
      const next = new URLSearchParams(searchParams);
      next.delete('override');
      next.delete('exchange');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Tickers shown in datalist: BUY → all active, SELL/HOLD → only held positions
  const tickerSuggestions = action === 'BUY' ? allTickers : heldPositions;

  // When action changes, clear ticker and unlock exchange
  const handleActionChange = (val: string) => {
    setAction(val);
    setTicker('');
    setExchange('');
    setExchangeLocked(false);
    setExchangeOptions([]);
    setQuantity('');
    setPositionData(null);
    setHoldReason('');
    setHoldUntil('');
    setIsPermanent(false);
  };

  // When ticker typed/selected: auto-fill and lock exchange (debounced 400ms)
  const handleTickerChange = (val: string) => {
    const t = val.toUpperCase();
    setTicker(t);
    setPositionData(null);
    setQuantity('');

    if (!t) {
      // Clearing ticker → unlock exchange
      if (tickerDebounceRef.current) clearTimeout(tickerDebounceRef.current);
      setExchange('');
      setExchangeLocked(false);
      setExchangeOptions([]);
      return;
    }

    // For SELL/HOLD: prefer exchange from held positions (avoids dual-listing ambiguity)
    if (action === 'SELL' || action === 'HOLD') {
      const held = heldPositions.find(p => p.ticker.toUpperCase() === t);
      if (held) {
        if (tickerDebounceRef.current) clearTimeout(tickerDebounceRef.current);
        setExchange(held.exchange);
        setExchangeLocked(true);
        setExchangeOptions([held.exchange]);
        return;
      }
    }

    if (tickerDebounceRef.current) clearTimeout(tickerDebounceRef.current);
    if (t.length < 2) return;

    tickerDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/admin/tickers/exchanges?ticker=${encodeURIComponent(t)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          const opts: string[] = d.exchanges || [];
          setExchangeOptions(opts);
          if (opts.length === 1) {
            setExchange(opts[0]);
            setExchangeLocked(true);
          } else if (opts.length > 1) {
            // Dual-listed: show dropdown, don't lock
            setExchange(opts[0]);
            setExchangeLocked(false);
          } else {
            setExchangeLocked(false);
          }
        } else {
          setExchangeLocked(false);
          setExchangeOptions([]);
        }
      } catch {
        setExchangeLocked(false);
        setExchangeOptions([]);
      }
    }, 400);
  };

  // Fetch position data when ticker + exchange are known
  useEffect(() => {
    if (!ticker || !exchange) { setPositionData(null); setPositionLoading(false); return; }
    const controller = new AbortController();
    setPositionData(null);
    setPositionLoading(true);
    fetch(`${API}/admin/portfolio/position?ticker=${encodeURIComponent(ticker)}&exchange=${encodeURIComponent(exchange)}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setPositionData(d); setPositionLoading(false); })
      .catch(e => { if (e.name !== 'AbortError') { setPositionData(null); setPositionLoading(false); } });
    return () => controller.abort();
  }, [ticker, exchange, token]);

  const maxQty = (() => {
    if (!positionData) return undefined;
    if (action === 'SELL') {
      return Math.max(0, positionData.qty_held - positionData.qty_pending_sell);
    }
    if (action === 'BUY' && positionData.current_price && positionData.current_price > 0) {
      const pendingBuyValue = positionData.qty_pending_buy * positionData.current_price;
      return Math.max(0, Math.floor((positionData.cash_available - pendingBuyValue) / positionData.current_price));
    }
    return undefined;
  })();

  const clearForm = () => {
    setTicker('');
    setExchange('');
    setExchangeLocked(false);
    setExchangeOptions([]);
    setQuantity('');
    setPositionData(null);
    setResult(null);
    setHoldReason('');
    setHoldUntil('');
    setIsPermanent(false);
  };

  const submit = async () => {
    if (!ticker || !exchange) return;
    if (action === 'HOLD') {
      setSubmitting(true);
      setResult(null);
      try {
        const res = await fetch(`${API}/admin/holds`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticker, exchange,
            hold_reason: holdReason || '',
            hold_until: isPermanent ? null : holdUntil || null,
            is_permanent: isPermanent,
          }),
        });
        const d = await res.json();
        if (d.success) {
          setResult({ ok: true, msg: d.message || 'Hold placed' });
          clearForm();
          loadHeldPositions();
        } else {
          setResult({ ok: false, msg: d.detail || d.message || 'Failed' });
        }
      } catch (e: any) {
        setResult({ ok: false, msg: e.message });
      }
      setSubmitting(false);
      return;
    }

    if (!quantity) return;
    const roundedQty = Math.round(parseFloat(quantity) * 100) / 100;
    const qty = roundedQty;
    // Hard block: enforce quantity limits before hitting API
    if (maxQty !== undefined && qty > maxQty) {
      if (action === 'SELL') {
        setResult({ ok: false, msg: `Quantity exceeds available shares (${maxQty} available)` });
      } else {
        setResult({ ok: false, msg: `Quantity exceeds what you can afford (max ${maxQty} shares)` });
      }
      return;
    }
    // Minimum position value check for SELL
    if (action === 'SELL' && positionData?.current_price) {
      const positionValue = qty * positionData.current_price;
      if (positionValue < 1.00) {
        setResult({ ok: false, msg: 'Position value too small to sell (minimum $1.00). Use broker platform directly for micro positions.' });
        return;
      }
    }
    // Penny stock warning (< $0.10) for BUY or SELL
    const currentPrice = positionData?.current_price;
    if ((action === 'BUY' || action === 'SELL') && currentPrice !== undefined && currentPrice !== null && currentPrice < 0.10) {
      if (!pennyStockWarning) {
        setPennyStockWarning({ price: currentPrice, ticker, action, qty });
        return;
      }
    }
    setPennyStockWarning(null);

    // Hold confirmation for SELL
    if (action === 'SELL') {
      const posKey = `${ticker}:${exchange}`;
      const holdInfo = holdsMap[posKey];
      if (holdInfo && !sellHoldWarning) {
        setSellHoldWarning({ holdInfo, qty });
        return;
      }
    }
    setSellHoldWarning(null);
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/admin/manual-order`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker, exchange, action, quantity: qty }),
      });
      const d = await res.json();
      if (d.success) {
        setResult({ ok: true, msg: d.message || 'Order placed', status: d.status, estimated: d.estimated_execution_time });
        clearForm();
        loadHeldPositions();
      } else {
        setResult({ ok: false, msg: d.detail || d.message || 'Failed' });
      }
    } catch (e: any) {
      setResult({ ok: false, msg: e.message });
    }
    setSubmitting(false);
  };

  return (
    <div className="border-t border-border-base p-4 space-y-4">
      <h2 className="font-semibold text-sm">Manual Override</h2>
      {botMode === 'passive' && (
        <div className="bg-status-amber-soft border border-border-amber rounded px-3 py-2 text-status-amber-muted text-xs">
          ⚠️ Bot is in passive mode. You are placing this order manually.
        </div>
      )}
      <p className="text-text-muted text-xs">Place a paper trade directly, bypassing the signal engine. Orders placed outside market hours are queued and executed at open.</p>

      {/* Order Form — at top */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-start">
        {/* 1. Action */}
        <div>
          <label className="text-text-secondary text-xs">Action</label>
          <select value={action} onChange={e => handleActionChange(e.target.value)}
            className="w-full bg-surface-2 border border-border-light rounded px-2 py-1.5 text-sm mt-1">
            <option>BUY</option>
            <option>SELL</option>
            <option>HOLD</option>
          </select>
          {action === 'SELL' && positionsWarning && (
            <p className="text-status-amber text-xs mt-0.5">Could not load positions — showing all tickers</p>
          )}
        </div>

        {/* 2. Ticker */}
        <div>
          <label className="text-text-secondary text-xs">Ticker</label>
          <input
            value={ticker}
            onChange={e => handleTickerChange(e.target.value)}
            list="manual-tickers-list"
            placeholder={action === 'SELL' ? 'Held ticker' : 'e.g. CBA'}
            className="w-full bg-surface-2 border border-border-light rounded px-2 py-1.5 text-sm mt-1"
          />
          <datalist id="manual-tickers-list">
            {tickerSuggestions.map(t => <option key={`${t.ticker}-${t.exchange}`} value={t.ticker} />)}
          </datalist>
        </div>

        {/* 3. Exchange */}
        <div>
          <label className="text-text-secondary text-xs">Exchange</label>
          {exchangeOptions.length > 1 ? (
            <>
              <select
                value={exchange}
                onChange={e => { setExchange(e.target.value); setPositionData(null); setQuantity(''); }}
                className="w-full bg-surface-2 border border-border-blue rounded px-2 py-1.5 text-sm mt-1"
              >
                {exchangeOptions.map(ex => <option key={ex} value={ex}>{ex}</option>)}
              </select>
              <p className="text-status-blue text-xs mt-0.5">Dual-listed — choose exchange for this order.</p>
            </>
          ) : (
            <>
              <input
                readOnly
                disabled={exchangeLocked}
                value={exchange}
                onChange={() => {}}
                placeholder="Auto-set from ticker"
                className={`w-full border rounded px-2 py-1.5 text-sm mt-1 ${
                  exchangeLocked
                    ? 'bg-surface-2 border-border-light text-text-primary cursor-not-allowed opacity-75'
                    : 'bg-surface-2 border-border-light text-text-secondary'
                }`}
              />
              {exchangeLocked && (
                <p className="text-text-muted text-xs mt-0.5">Exchange auto-set. Clear ticker to change.</p>
              )}
            </>
          )}
        </div>

        {/* 4. Quantity (hidden for HOLD) or Hold fields */}
        {action === 'HOLD' ? (
          <>
            <div>
              <label className="text-text-secondary text-xs">Hold Reason (optional)</label>
              <input
                value={holdReason}
                onChange={e => setHoldReason(e.target.value)}
                placeholder="e.g. Earnings pending"
                className="w-full bg-surface-2 border border-border-light rounded px-2 py-1.5 text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-text-secondary text-xs">Hold Until (optional)</label>
              <input
                type="date"
                value={holdUntil}
                onChange={e => setHoldUntil(e.target.value)}
                disabled={isPermanent}
                className="w-full bg-surface-2 border border-border-light rounded px-2 py-1.5 text-sm mt-1 disabled:opacity-40"
              />
              <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
                <input type="checkbox" checked={isPermanent} onChange={e => setIsPermanent(e.target.checked)} className="accent-status-amber" />
                <span className="text-text-secondary text-xs">Permanent hold</span>
              </label>
            </div>
          </>
        ) : (
          <div>
            <label className="text-text-secondary text-xs">Quantity (shares)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={maxQty !== undefined ? maxQty : undefined}
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              placeholder="100"
              disabled={action === 'SELL' && positionData !== null && maxQty === 0}
              className="w-full bg-surface-2 border border-border-light rounded px-2 py-1.5 text-sm mt-1 disabled:opacity-50"
            />
            {positionData && action === 'SELL' && (
              maxQty === 0
                ? <p className="text-status-red text-xs mt-0.5">No shares available to sell</p>
                : <p className="text-text-muted text-xs mt-0.5">Held: {positionData.qty_held.toFixed(2)} ({positionData.qty_pending_sell} pending sell) — max {maxQty != null ? maxQty.toFixed(2) : '?'}</p>
            )}
            {positionData && action === 'BUY' && (
              positionData.current_price
                ? <p className="text-text-muted text-xs mt-0.5">Cash: ${positionData.cash_available.toFixed(2)} @ ${positionData.current_price.toFixed(4)}/share — max {maxQty !== undefined ? maxQty.toFixed(2) : '?'} shares</p>
                : <p className="text-text-muted text-xs mt-0.5">Cash: ${positionData.cash_available.toFixed(2)}</p>
            )}
            {positionLoading && (
              <p className="text-text-secondary text-xs mt-0.5">Loading...</p>
            )}
          </div>
        )}

        {/* 5. Submit */}
        <button
          onClick={submit}
          disabled={
            submitting ||
            positionLoading ||
            !ticker ||
            !exchange ||
            exchange === 'US' ||
            (action !== 'HOLD' && positionData === null) ||
            (action !== 'HOLD' && (!quantity || (action === 'SELL' && maxQty === 0)))
          }
          className={`${action === 'HOLD' ? 'bg-status-amber-btn hover:bg-status-amber-btn-hover' : 'bg-status-green-btn hover:bg-status-green-btn-hover'} disabled:opacity-50 text-text-bright px-4 py-1.5 rounded text-sm font-medium mt-5`}
        >
          {submitting ? 'Placing...' : action === 'HOLD' ? 'Place Hold' : 'Initiate Action'}
        </button>
        {exchange === 'US' && (
          <p className="text-status-red text-xs mt-1">
            Exchange not resolved — cannot place order. Please wait or re-select ticker.
          </p>
        )}
        {action !== 'HOLD' && ticker && exchange && exchange !== 'US' && positionData === null && (
          <p className="text-status-amber text-xs mt-1">
            Loading position data — please wait before placing order.
          </p>
        )}

        {/* 6. Clear */}
        <button onClick={clearForm} type="button"
          className="border border-border-light hover:border-border-light text-text-secondary hover:text-text-primary px-4 py-1.5 rounded text-sm font-medium mt-5 bg-transparent">
          Clear
        </button>
      </div>

      {result && (
        <div className={`text-xs ${result.ok ? 'text-status-green' : 'text-status-red'}`}>
          <span>{result.msg}</span>
          {result.ok && result.status && (
            <span className="ml-2 text-text-secondary">
              [{result.status === 'executed' ? 'Executed immediately' : `Queued — ${result.estimated || 'pending market open'}`}]
            </span>
          )}
        </div>
      )}

      {/* Penny stock warning popup */}
      {pennyStockWarning && (
        <div className="bg-status-amber-soft border border-border-amber rounded p-3 text-sm space-y-2">
          <p className="text-status-amber-muted font-semibold">⚠️ Penny Stock Warning</p>
          <p className="text-text-primary text-xs">
            {pennyStockWarning.ticker} is currently priced at ${pennyStockWarning.price.toFixed(4)} which is below the $0.10 minimum threshold.
            Penny stocks carry significantly higher risk including low liquidity and high volatility.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPennyStockWarning(null)}
              className="bg-surface-2 hover:bg-surface-hover text-text-bright px-3 py-1 rounded text-xs"
            >
              Cancel
            </button>
            <button
              onClick={() => submit()}
              className="bg-status-amber-btn hover:bg-status-amber-btn-hover text-text-bright px-3 py-1 rounded text-xs font-semibold"
            >
              Continue Anyway
            </button>
          </div>
        </div>
      )}

      {/* Hold sell confirmation popup */}
      {sellHoldWarning && (
        <div className="bg-status-amber-soft border border-border-amber rounded p-3 text-sm space-y-2">
          <p className="text-status-amber-muted font-semibold">⚠️ {ticker} is currently on hold. Are you sure you want to sell?</p>
          {sellHoldWarning.holdInfo.hold_reason && (
            <p className="text-text-primary text-xs">Reason: {sellHoldWarning.holdInfo.hold_reason}</p>
          )}
          <p className="text-text-secondary text-xs">
            Until: {sellHoldWarning.holdInfo.is_permanent ? 'Permanent' : (sellHoldWarning.holdInfo.hold_until || 'Not set')}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setSellHoldWarning(null)}
              className="bg-surface-2 hover:bg-surface-hover text-text-bright px-3 py-1 rounded text-xs"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                // Remove hold then submit order
                try {
                  await fetch(`${API}/admin/holds/${encodeURIComponent(`${ticker}:${exchange}`)}`, {
                    method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
                  });
                } catch {}
                submit();
              }}
              className="bg-status-red-btn hover:bg-status-red-btn-hover text-text-bright px-3 py-1 rounded text-xs font-semibold"
            >
              Sell Anyway
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Trade History Panel ──────────────────────────────────────────────────────

function TradeHistoryPanel({ token, refreshTrigger }: { token: string; refreshTrigger: number }) {
  const [trades, setTrades] = useState<TradeRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      fetch(`${API}/trades?limit=100`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { trades: [] })
        .then(d => { setTrades(d.trades || []); setLoading(false); })
        .catch(() => setLoading(false));
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [token, refreshTrigger]);

  const fmtDuration = (hours: number | null) => {
    if (hours === null || hours === undefined) return '—';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const fmtDT = (isoStr: string | null) => {
    if (!isoStr) return { date: '—', time: '' };
    const d = new Date(isoStr);
    return {
      date: d.toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: '2-digit', month: 'short', year: 'numeric' }),
      time: d.toLocaleTimeString('en-AU', { timeZone: 'Australia/Brisbane', hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-4 border-b border-gray-700">
        <h2 className="font-semibold">Trade History</h2>
        <p className="text-gray-500 text-xs mt-0.5">Executed trades from DB · Up to 100 most recent</p>
      </div>
      {loading ? (
        <div className="p-4 text-gray-400 text-sm">Loading...</div>
      ) : trades.length === 0 ? (
        <div className="p-4 text-gray-400 text-sm">No trades yet</div>
      ) : (
        <div className="overflow-y-auto" style={{ maxHeight: '480px' }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-800 z-10">
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left p-2 whitespace-nowrap">Date/Time</th>
                <th className="text-left p-2">Ticker</th>
                <th className="text-left p-2">Exchange</th>
                <th className="text-center p-2">Action</th>
                <th className="text-right p-2">Qty</th>
                <th className="text-right p-2 whitespace-nowrap">Entry Price</th>
                <th className="text-right p-2 whitespace-nowrap">Exit Price</th>
                <th className="text-right p-2">P&L</th>
                <th className="text-right p-2">Duration</th>
                <th className="text-right p-2 whitespace-nowrap">Buy Score</th>
                <th className="text-right p-2 whitespace-nowrap">Sell Score</th>
                <th className="text-center p-2">Manual</th>
                <th className="text-left p-2 whitespace-nowrap">Submitted / Reason</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => {
                const { date, time } = fmtDT(t.executed_at);
                const isManual = (t.notes || '').toLowerCase().includes('manual');
                const isFailed = t.status === 'failed';
                return (
                  <tr key={i} className={`border-b border-gray-700/50 ${isFailed ? 'opacity-60' : ''}`}>
                    <td className="p-2 whitespace-nowrap">
                      <div>{date}</div>
                      <div className="text-gray-500">{time} AEST</div>
                    </td>
                    <td className="p-2 font-medium text-green-400">{t.ticker}</td>
                    <td className="p-2 text-gray-300">{t.exchange}</td>
                    <td className="p-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                        t.action === 'BUY' ? 'bg-green-900 text-green-300' :
                        t.action === 'SELL' ? 'bg-red-900 text-red-300' :
                        'bg-yellow-900 text-yellow-300'
                      }`}>{t.action}</span>
                    </td>
                    <td className="p-2 text-right">{t.quantity != null ? Number(t.quantity).toFixed(0) : '—'}</td>
                    <td className="p-2 text-right">
                      {t.action === 'SELL' && t.avg_cost_at_trade != null
                        ? `$${Number(t.avg_cost_at_trade).toFixed(4)}`
                        : t.action === 'BUY' && t.price != null
                        ? `$${Number(t.price).toFixed(4)}`
                        : '—'}
                    </td>
                    <td className="p-2 text-right">
                      {t.action === 'SELL' && t.price != null ? `$${Number(t.price).toFixed(4)}` : '—'}
                    </td>
                    <td className="p-2 text-right">
                      {t.pnl != null && t.action === 'SELL' ? (
                        <span className={t.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {t.pnl >= 0 ? '+' : '-'}${Math.abs(t.pnl).toFixed(2)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="p-2 text-right">{t.action === 'SELL' ? fmtDuration(t.duration_hours) : '—'}</td>
                    <td className="p-2 text-right">{t.buy_signal_score != null ? Number(t.buy_signal_score).toFixed(1) : '—'}</td>
                    <td className="p-2 text-right">{t.action === 'SELL' && t.signal_score != null ? Number(t.signal_score).toFixed(1) : '—'}</td>
                    <td className="p-2 text-center">{isManual ? '✓' : ''}</td>
                    <td className="p-2">
                      {isFailed ? (
                        <>
                          <div className="text-gray-400">{date}</div>
                          <div className="text-red-400">Fail: {t.notes || '—'}</div>
                        </>
                      ) : t.action === 'CANCELLED' ? (
                        <div className="text-gray-400 text-sm">{t.notes || 'Cancelled'}</div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Exchange Tab Content ─────────────────────────────────────────────────────

function ExchangeTabContent({
  exchange, alpacaAccount, asxAccount, positionSignals,
  asxDailyPnl, asxDailyPnlPct, dbPositions, token, onHoldRemoved, summary,
}: {
  exchange: string;
  alpacaAccount: AlpacaAccount | null;
  asxAccount: ASXAccount | null;
  summary: { asx_cash?: number; asx_equity?: number; asx_positions_value?: number } | null;
  positionSignals: Record<string, PositionSignal>;
  asxDailyPnl: number | null;
  asxDailyPnlPct: number | null;
  dbPositions: DbPosition[];
  token: string;
  onHoldRemoved: () => void;
}) {
  const navigate = useNavigate();

  if (exchange === 'ASX') {
    const acc = asxAccount;
    const tabPositions = dbPositions.filter(p => p.market === 'ASX');
    const todayAEST = new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short' });
    const unrealPnl = tabPositions.reduce((s, p) => s + (p.unrealised_pnl || 0), 0);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard label="Total Value" value={summary ? `$${fmt(summary.asx_equity ?? 0)} AUD` : '—'} sub="ASX (PaperBroker)" />
          <SummaryCard label="Cash" value={summary ? `$${fmt(summary.asx_cash ?? 0)} AUD` : '—'} sub="Available" />
          <SummaryCard label="Invested" value={summary ? `$${fmt(summary.asx_positions_value ?? 0)} AUD` : '—'} sub="In positions" />
          <SummaryCard label="Positions" value={tabPositions.length} sub="Open" />
          <SummaryCard
            label="Unrealised P&L"
            value={<PnLValue value={unrealPnl} showPct={false} suffix="AUD" />}
            sub={acc ? `${acc.total_pnl >= 0 ? '+' : ''}${acc.total_pnl_pct?.toFixed(2) ?? '0.00'}%` : ''}
          />
          <SummaryCard
            label={`Daily P&L (${todayAEST})`}
            value={asxDailyPnl === null ? <span className="text-gray-500">…</span> : <PnLValue value={asxDailyPnl} showPct={false} suffix="AUD" />}
            sub={asxDailyPnlPct !== null ? `${asxDailyPnlPct >= 0 ? '+' : ''}${asxDailyPnlPct.toFixed(2)}%` : 'Loading…'}
          />
        </div>
        <div className="text-xs text-text-muted flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-status-green inline-block" />
          Positions sourced from DB · Prices updated by market data scheduler
        </div>
        <PositionsTable positions={tabPositions} positionSignals={positionSignals} navigate={navigate} token={token} onHoldRemoved={onHoldRemoved} label="ASX Positions" />
      </div>
    );
  }

  if (exchange === 'US' || exchange === 'NASDAQ' || exchange === 'NYSE') {
    const acc = alpacaAccount;
    const tabPositions = dbPositions.filter(p => p.market === 'US');
    const invested = (acc?.equity ?? 0) - (acc?.cash ?? 0);
    const unrealPnl = tabPositions.reduce((s, p) => s + (p.unrealised_pnl || 0), 0);
    const todayAEST = new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short' });
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard label="Total Value" value={acc ? `$${fmt(acc.portfolio_value)} USD` : '—'} sub="US Paper" />
          <SummaryCard label="Cash" value={acc ? `$${fmt(acc.cash)} USD` : '—'} sub="Available" />
          <SummaryCard label="Invested" value={acc ? `$${fmt(invested)} USD` : '—'} sub="In positions" />
          <SummaryCard label="Positions" value={tabPositions.length} sub="Open" />
          <SummaryCard
            label="Unrealised P&L"
            value={<PnLValue value={unrealPnl} showPct={false} suffix="USD" />}
            sub="All positions"
          />
          <SummaryCard
            label={`Daily P&L (${todayAEST})`}
            value={acc ? <PnLValue value={acc.daily_pnl} showPct={false} suffix="USD" /> : '—'}
            sub={acc ? `${acc.daily_pnl >= 0 ? '+' : ''}${acc.daily_pnl_pct?.toFixed(2) ?? '0.00'}%` : ''}
          />
        </div>
        <div className="text-xs text-text-muted flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-status-blue inline-block" />
          Positions sourced from DB · Account summary from Alpaca
        </div>
        <PositionsTable positions={tabPositions} positionSignals={positionSignals} navigate={navigate} token={token} onHoldRemoved={onHoldRemoved} label="NYSE/NASDAQ Positions" />
      </div>
    );
  }

  return <div className="text-gray-400 text-sm p-4">No data available for {exchange}</div>;
}

function HoldPopup({ position, token, onClose, onRemoved }: {
  position: DbPosition;
  token: string;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const [removing, setRemoving] = useState(false);

  const removeHold = async () => {
    if (!window.confirm(`Are you sure you want to remove the hold on ${position.ticker}? The bot will resume automatic trading.`)) return;
    setRemoving(true);
    try {
      const res = await fetch(`${API}/admin/holds/${encodeURIComponent(position.position_id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        onRemoved();
        onClose();
      } else {
        const d = await res.json();
        alert(d.detail || 'Failed to remove hold');
      }
    } catch (e: any) { alert(e.message); }
    setRemoving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-sm w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">🔒 Hold — {position.ticker}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
        </div>
        <div className="space-y-2 text-sm text-gray-300">
          <div><span className="text-gray-500">Reason:</span> {position.hold_reason || '(none)'}</div>
          <div>
            <span className="text-gray-500">Duration:</span>{' '}
            {position.is_permanent
              ? 'Permanent'
              : position.hold_until
              ? `Until ${new Date(position.hold_until).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`
              : 'Permanent'}
          </div>
        </div>
        <button onClick={removeHold} disabled={removing}
          className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white py-2 rounded text-sm font-medium">
          {removing ? 'Removing...' : 'Remove Hold'}
        </button>
      </div>
    </div>
  );
}

function PositionsTable({ positions, positionSignals, navigate, token, onHoldRemoved, label }: {
  positions: DbPosition[];
  positionSignals: Record<string, PositionSignal>;
  navigate: ReturnType<typeof useNavigate>;
  token: string;
  onHoldRemoved: () => void;
  label?: string;
}) {
  const [holdPopup, setHoldPopup] = useState<DbPosition | null>(null);
  const [removingHold, setRemovingHold] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(true);

  const removeHold = async (pos: DbPosition, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to remove the hold on ${pos.ticker}? The bot will resume automatic trading.`)) return;
    setRemovingHold(pos.position_id);
    try {
      const res = await fetch(`${API}/admin/holds/${encodeURIComponent(pos.position_id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        onHoldRemoved();
      } else {
        const d = await res.json();
        alert(d.detail || 'Failed to remove hold');
      }
    } catch (e: any) { alert(e.message); }
    setRemovingHold(null);
  };

  const displayLabel = label || 'Positions';
  const count = positions.length;

  return (
    <>
      {holdPopup && (
        <HoldPopup
          position={holdPopup}
          token={token}
          onClose={() => setHoldPopup(null)}
          onRemoved={() => { onHoldRemoved(); setHoldPopup(null); }}
        />
      )}
      <div className="bg-surface rounded-card border border-border-base">
        <button
          onClick={() => setCollapsed(c => !c)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-text-primary hover:bg-surface-hover rounded-card"
        >
          <span className="font-medium">{displayLabel} ({count})</span>
          <span className="text-text-secondary text-xs">{collapsed ? '▶' : '▼'}</span>
        </button>
        {!collapsed && (
          count === 0 ? (
            <div className="px-3 pb-3 text-text-secondary text-sm">No open positions</div>
          ) : (
            <div className="overflow-x-auto" style={{ maxHeight: '370px', overflowY: 'auto' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-text-secondary border-b border-border-base text-xs uppercase sticky top-0 bg-surface">
                    <th className="text-left p-3">Ticker</th>
                    <th className="text-left p-3">Exchange</th>
                    <th className="text-right p-3">Qty</th>
                    <th className="text-right p-3">Avg Cost</th>
                    <th className="text-right p-3">Market Price</th>
                    <th className="text-right p-3">Market Value</th>
                    <th className="text-right p-3">Unrealised P&L</th>
                    <th className="text-right p-3">Score</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos, i) => (
                    <tr key={i} className="border-b border-border-base hover:bg-surface-hover">
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/explorer?ticker=${pos.ticker}&exchange=${pos.exchange}`)}
                            className="font-medium text-status-green hover:text-status-green-muted underline underline-offset-2"
                          >
                            {pos.ticker}
                          </button>
                          {pos.is_held && (
                            <button onClick={() => setHoldPopup(pos)}
                              className="text-status-amber hover:text-status-amber-muted text-xs" title="Held — click for details">
                              🔒
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-xs text-text-secondary">{pos.exchange}</td>
                      <td className="p-3 text-right">{(pos.qty_held || 0).toFixed(2)}</td>
                      <td className="p-3 text-right">${(pos.avg_cost || 0).toFixed(4)}</td>
                      <td className="p-3 text-right">${(pos.current_price || 0).toFixed(4)}</td>
                      <td className="p-3 text-right">${fmt(pos.market_value || 0)}</td>
                      <td className="p-3 text-right">
                        <PnLValue value={pos.unrealised_pnl || 0} />
                      </td>
                      <td className="p-3 text-right">
                        {pos.signal_score != null ? (
                          <span className={`font-medium ${pos.signal_score >= 6.5 ? 'text-status-green' : pos.signal_score <= 3.5 ? 'text-status-red' : 'text-status-amber'}`}>
                            {Number(pos.signal_score).toFixed(1)}
                          </span>
                        ) : <span className="text-text-muted">—</span>}
                      </td>
                      <td className="p-3 text-right">
                        {pos.is_held && (
                          <button
                            onClick={(e) => removeHold(pos, e)}
                            disabled={removingHold === pos.position_id}
                            className="text-status-amber hover:text-status-red disabled:opacity-50 text-xs px-1.5 py-0.5 border border-border-amber hover:border-border-red rounded"
                            title="Remove hold"
                          >🔒✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </>
  );
}

// ─── Top Signals Leaderboard (Session 6c — replaces Recent Signals) ──────────

interface TopSignalTicker {
  ticker: string;
  exchange: string;
  specific_exchange?: string | null;
  signal_score: number | null;
}

const TOP_SIGNALS_MIN_SCORE = 7.01;
const TOP_SIGNALS_EXCHANGES = ['ALL', 'ASX', 'NASDAQ', 'NYSE', 'AMEX', 'NYSE Arca'] as const;
const TOP_SIGNALS_US_SPECIFIC = new Set(['NYSE', 'NASDAQ', 'AMEX', 'NYSE ARCA', 'CHI-X']);

function topSignalMatchesExchange(t: TopSignalTicker, filter: string): boolean {
  if (filter === 'ALL') return true;
  const up = filter.toUpperCase();
  if (TOP_SIGNALS_US_SPECIFIC.has(up)) return (t.specific_exchange || '').toUpperCase() === up;
  return (t.exchange || '').toUpperCase() === up;
}

function TopSignalsLeaderboard({ token, onInfoClick }: { token: string; onInfoClick: () => void }) {
  const navigate = useNavigate();
  const [tickers, setTickers] = useState<TopSignalTicker[]>([]);
  const [loading, setLoading] = useState(true);
  const [exchangeFilter, setExchangeFilter] = useState<string>('ALL');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [breakdowns, setBreakdowns] = useState<Record<string, Signal | null>>({});
  const [breakdownLoading, setBreakdownLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api(`/tickers?sort=score_desc&min_score=${TOP_SIGNALS_MIN_SCORE}`, token);
      if (res.ok) {
        const d = await res.json();
        setTickers(d.tickers || []);
        setLastUpdated(new Date());
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = tickers.filter(t => topSignalMatchesExchange(t, exchangeFilter));

  const toggleExpand = async (row: TopSignalTicker) => {
    const key = `${row.ticker}:${row.exchange}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (breakdowns[key] !== undefined) return;
    setBreakdownLoading(key);
    try {
      const res = await api(`/signals/stored?tickers=${encodeURIComponent(row.ticker)}`, token);
      if (res.ok) {
        const d = await res.json();
        const stored = d.signals?.[row.ticker];
        setBreakdowns(prev => ({
          ...prev,
          [key]: stored ? {
            ticker: row.ticker,
            market: row.exchange,
            action: stored.action,
            confidence: stored.confidence,
            rationale: '',
            created_at: stored.created_at,
            valuation_score: stored.valuation_score,
            quality_score: stored.quality_score,
            momentum_score: stored.momentum_score,
            sentiment_score: stored.sentiment_score,
            macro_score: stored.macro_score,
            final_score: stored.final_score,
            breakdown: stored.breakdown,
          } : null,
        }));
      }
    } finally {
      setBreakdownLoading(null);
    }
  };

  return (
    <div className="bg-surface rounded-card border border-border-base">
      <div className="p-4 border-b border-border-base flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center space-x-2">
          <h2 className="font-semibold">Top Signals</h2>
          <button
            onClick={onInfoClick}
            className="w-5 h-5 rounded-full bg-surface-2 hover:bg-status-blue-btn text-text-primary hover:text-text-bright text-xs flex items-center justify-center transition-colors"
          >i</button>
        </div>
        <span className="text-xs text-text-muted">
          Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString('en-AU', { hour12: false }) : '—'}
        </span>
      </div>
      <div className="px-4 pt-3 flex gap-1 flex-wrap">
        {TOP_SIGNALS_EXCHANGES.map(ex => (
          <button key={ex} onClick={() => setExchangeFilter(ex)}
            className={`text-xs px-3 py-1 rounded ${exchangeFilter === ex ? 'bg-status-blue-btn text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-surface-hover'}`}>
            {ex}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="p-4 text-text-secondary text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="p-4 text-text-secondary text-sm">No scored signals yet</div>
      ) : (
        <div className="mt-3">
          <ScrollWindow showRevealButton={false}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b border-border-base text-xs uppercase sticky top-0 bg-surface">
                  <th className="text-left p-3">Ticker</th>
                  <th className="text-left p-3">Exchange</th>
                  <th className="text-right p-3">Score</th>
                  <th className="text-right p-3">Expand</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const key = `${row.ticker}:${row.exchange}`;
                  const isExpanded = expandedKey === key;
                  const score = row.signal_score;
                  const scoreColor = score == null ? 'text-text-muted' : score >= 6.5 ? 'text-status-green' : score <= 3.5 ? 'text-status-red' : 'text-status-amber';
                  const bd = breakdowns[key];
                  return (
                    <React.Fragment key={key}>
                      <tr className="border-b border-border-base hover:bg-surface-hover">
                        <td className="p-3">
                          <button
                            onClick={() => navigate(`/explorer?ticker=${row.ticker}&exchange=${row.exchange === 'ASX' ? 'ASX' : 'US'}`)}
                            className="font-medium text-status-green hover:text-status-green-muted underline underline-offset-2"
                          >
                            {row.ticker}
                          </button>
                        </td>
                        <td className="p-3 text-xs text-text-secondary">{row.specific_exchange || row.exchange}</td>
                        <td className={`p-3 text-right font-medium ${scoreColor}`}>{score != null ? score.toFixed(2) : '—'}</td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => toggleExpand(row)}
                            className="text-xs text-text-muted hover:text-text-primary transition-colors"
                          >
                            {isExpanded ? '▲' : '▼'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-border-base bg-surface-2">
                          <td colSpan={4} className="p-3">
                            {breakdownLoading === key ? (
                              <p className="text-text-muted text-xs italic">Loading breakdown...</p>
                            ) : bd ? (
                              formatBreakdownPlainEnglish(bd.breakdown || {}, bd)
                            ) : (
                              <p className="text-text-muted text-xs italic">Detailed breakdown not available for this signal</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </ScrollWindow>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

function Dashboard() {
  const token = sessionStorage.getItem('token') || '';
  const navigate = useNavigate();
  const [alpacaAccount, setAlpacaAccount] = useState<AlpacaAccount | null>(null);
  const [alpacaPositions, setAlpacaPositions] = useState<AlpacaPosition[]>([]);
  const [asxAccount, setAsxAccount] = useState<ASXAccount | null>(null);
  const [summary, setSummary] = useState<{ asx_cash: number; asx_equity: number; asx_positions_value: number } | null>(null);
  const [asxPositions, setAsxPositions] = useState<ASXPosition[]>([]);
  const [dbPositions, setDbPositions] = useState<DbPosition[]>([]);
  const [asxDailyPnl, setAsxDailyPnl] = useState<number | null>(null);
  const [asxDailyPnlPct, setAsxDailyPnlPct] = useState<number | null>(null);
  const [positionSignals, setPositionSignals] = useState<Record<string, PositionSignal>>({});
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [healthFlags, setHealthFlags] = useState<string[]>([]);
  const [healthBannerDismissed, setHealthBannerDismissed] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('ASX');
  const [marketStatuses, setMarketStatuses] = useState<Record<string, MarketStatus>>({});
  const [clockTime, setClockTime] = useState(new Date());
  const [openOrders, setOpenOrders] = useState<{ asx: OpenOrder[]; us: OpenOrder[] }>({ asx: [], us: [] });
  const [activeHolds, setActiveHolds] = useState<ActiveHold[]>([]);
  const [tickerExchangeMap, setTickerExchangeMap] = useState<Record<string, string>>({});
  const [tradeRefreshTrigger, setTradeRefreshTrigger] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setClockTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api('/admin/tickers/list', token)
      .then(r => r.ok ? r.json() : [])
      .then((rows: Array<{ ticker: string; exchange: string; specific_exchange?: string }>) => {
        const map: Record<string, string> = {};
        for (const r of rows) if (r.ticker) map[r.ticker.toUpperCase()] = r.specific_exchange || r.exchange;
        setTickerExchangeMap(map);
      })
      .catch(() => {});
  }, [token]);

  const loadMarketStatus = useCallback(async () => {
    try {
      const res = await api('/admin/market-status', token);
      if (res.ok) {
        const d = await res.json();
        const map: Record<string, MarketStatus> = {};
        for (const m of (d.markets || [])) { map[m.exchange] = m; }
        setMarketStatuses(map);
      }
    } catch {}
  }, [token]);

  const loadData = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && _portfolioCache && Date.now() - _portfolioCache.fetchedAt < CACHE_TTL) {
      setAlpacaAccount(_portfolioCache.alpacaAccount);
      setAlpacaPositions(_portfolioCache.alpacaPositions);
      setAsxAccount(_portfolioCache.asxAccount);
      setSummary(_portfolioCache.summary);
      setAsxPositions(_portfolioCache.asxPositions);
      setPositionSignals(_portfolioCache.positionSignals);
      setLoading(false);
      return;
    }
    try {
      const [summaryRes, asxAccRes, asxPosRes] = await Promise.allSettled([
        api('/portfolio/summary', token),
        api('/asx-paper/account', token),
        api('/asx-paper/positions', token),
      ]);

      let usTickers: string[] = [];
      let asxTickers: string[] = [];
      let newAlpacaAccount: AlpacaAccount | null = null;
      let newAlpacaPositions: AlpacaPosition[] = [];
      let newAsxAccount: ASXAccount | null = null;
      let newAsxPositions: ASXPosition[] = [];
      let newSummary: { asx_cash: number; asx_equity: number; asx_positions_value: number } | null = null;

      if (summaryRes.status === 'fulfilled' && summaryRes.value.ok) {
        const data = await summaryRes.value.json();
        newAlpacaAccount = {
          portfolio_value: data.portfolio_value ?? 0,
          cash: data.cash ?? 0,
          buying_power: data.buying_power ?? 0,
          equity: data.portfolio_value ?? 0,
          daily_pnl: data.day_pnl ?? 0,
          daily_pnl_pct: data.day_pnl_pct ?? 0,
          position_count: 0,
          positions: [],
        };
        newSummary = {
          asx_cash: data.asx_cash ?? 0,
          asx_equity: data.asx_equity ?? 0,
          asx_positions_value: data.asx_positions_value ?? 0,
        };
        setAlpacaAccount(newAlpacaAccount);
        setSummary(newSummary);
      }
      if (asxAccRes.status === 'fulfilled' && asxAccRes.value.ok) {
        newAsxAccount = await asxAccRes.value.json();
        setAsxAccount(newAsxAccount);
      }
      if (asxPosRes.status === 'fulfilled' && asxPosRes.value.ok) {
        const data = await asxPosRes.value.json();
        newAsxPositions = data.positions || [];
        setAsxPositions(newAsxPositions);
        asxTickers = newAsxPositions.map((p: ASXPosition) => p.ticker);
      }

      const allTickers = Array.from(new Set([...usTickers, ...asxTickers]));
      let newPositionSignals: Record<string, PositionSignal> = {};
      if (allTickers.length > 0) {
        try {
          const sigRes = await api(`/signals/stored?tickers=${allTickers.join(',')}`, token);
          if (sigRes.ok) {
            const sigData = await sigRes.json();
            newPositionSignals = sigData.signals || {};
            setPositionSignals(newPositionSignals);
          }
        } catch {}
      }
      _portfolioCache = {
        summary: newSummary,
        alpacaAccount: newAlpacaAccount,
        alpacaPositions: newAlpacaPositions,
        asxAccount: newAsxAccount,
        asxPositions: newAsxPositions,
        positionSignals: newPositionSignals,
        fetchedAt: Date.now(),
      };
    } catch (e) { console.error('Dashboard load error:', e); }
    finally { setLoading(false); }
  }, [token]);

  const loadHealthFlags = useCallback(async () => {
    try {
      const res = await api('/health/daily-report', token);
      if (res.ok) {
        const d = await res.json();
        if (d.report?.checks) {
          const failed = (d.report.checks as Array<{check: string; ok: boolean}>)
            .filter(c => !c.ok)
            .map(c => c.check);
          setHealthFlags(failed);
          if (failed.length > 0) setHealthBannerDismissed(false);
        }
      }
    } catch {}
  }, [token]);

  const loadAsxDailyPnl = useCallback(async () => {
    try {
      const res = await api('/asx-paper/daily-pnl', token);
      if (res.ok) {
        const d = await res.json();
        setAsxDailyPnl(d.daily_pnl ?? null);
        setAsxDailyPnlPct(d.daily_pnl_pct ?? null);
      }
    } catch {}
  }, [token]);

  const loadDbPositions = useCallback(async () => {
    try {
      const res = await api('/portfolio/positions', token);
      if (res.ok) {
        const d = await res.json();
        setDbPositions(d.positions || []);
      }
    } catch {}
  }, [token]);

  const loadOpenOrders = useCallback(async () => {
    try {
      const res = await api('/portfolio/open-orders', token);
      if (res.ok) {
        const d = await res.json();
        setOpenOrders({ asx: d.asx || [], us: d.us || [] });
      }
    } catch {}
  }, [token]);

  const loadActiveHolds = useCallback(async () => {
    try {
      const res = await api('/admin/holds', token);
      if (res.ok) {
        const d = await res.json();
        setActiveHolds(d.holds || []);
      }
    } catch {}
  }, [token]);

  const refreshOrdersAndHolds = useCallback(() => {
    loadOpenOrders();
    loadActiveHolds();
  }, [loadOpenOrders, loadActiveHolds]);

  useEffect(() => {
    loadData();
    loadMarketStatus();
    loadHealthFlags();
    loadAsxDailyPnl();
    loadDbPositions();
    loadOpenOrders();
    loadActiveHolds();
    const i1 = setInterval(loadData, 60000);
    const i2 = setInterval(loadMarketStatus, 60000);
    const i3 = setInterval(loadHealthFlags, 300000);
    const i4 = setInterval(loadAsxDailyPnl, 120000);
    const i5 = setInterval(loadDbPositions, 60000);
    const i6 = setInterval(loadOpenOrders, 30000);
    const i7 = setInterval(loadActiveHolds, 30000);
    return () => { clearInterval(i1); clearInterval(i2); clearInterval(i3); clearInterval(i4); clearInterval(i5); clearInterval(i6); clearInterval(i7); };
  }, [loadData, loadMarketStatus, loadHealthFlags, loadAsxDailyPnl, loadDbPositions, loadOpenOrders, loadActiveHolds]);

  const alpacaValue = alpacaAccount?.portfolio_value ?? 0;
  const asxValue = summary?.asx_equity ?? 0;
  const combinedValue = alpacaValue + asxValue;
  const alpacaCash = alpacaAccount?.cash ?? 0;
  const asxCash = summary?.asx_cash ?? 0;
  const combinedCash = alpacaCash + asxCash;
  const alpacaUnrealisedPnl = alpacaPositions.reduce((sum, p) => sum + (p.unrealised_pnl || 0), 0);
  const asxUnrealisedPnl = dbPositions.filter(p => p.market === 'ASX').reduce((sum, p) => sum + (p.unrealised_pnl || 0), 0);
  // alpacaPositions is never populated by loadData (always []) — use dbPositions (from /portfolio/positions) for US instead.
  const usUnrealisedPnl = dbPositions.filter(p => p.market === 'US').reduce((sum, p) => sum + (p.unrealised_pnl || 0), 0);
  const combinedUnrealisedPnl = alpacaUnrealisedPnl + asxUnrealisedPnl;
  const alpacaInvested = (alpacaAccount?.equity ?? 0) - alpacaCash;
  const asxInvested = summary?.asx_positions_value ?? 0;
  const combinedInvested = alpacaInvested + asxInvested;
  const asxPortfolioValue = asxCash + asxInvested;
  const usPortfolioValue = alpacaCash + alpacaInvested;

  const allPositions = dbPositions.map(p => ({ ticker: p.ticker, exchange: p.exchange }));

  const exchangeTabs = [
    { code: 'ASX', region: 'AU', name: 'ASX', mode: 'PAPER' },
    { code: 'US', region: 'US', name: 'NYSE/NASDAQ', mode: 'PAPER' },
  ];

  const activeMarketStatus = marketStatuses[activeTab === 'US' ? 'NASDAQ' : activeTab] || null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Dashboard</h1>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-gray-400 text-sm">
            {clockTime.toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' })} AEST
          </span>
          <button onClick={() => { loadData(true); loadMarketStatus(); }}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-3 py-1 rounded">
            Refresh
          </button>
        </div>
      </div>

      {/* Health flags banner */}
      {!healthBannerDismissed && healthFlags.length > 0 && (
        <div className="bg-amber-900 border border-amber-700 rounded-lg px-4 py-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-amber-200 text-sm font-semibold">System Health Issues Detected</p>
            <p className="text-amber-300 text-xs mt-1">{healthFlags.join(' · ')}</p>
          </div>
          <button onClick={() => setHealthBannerDismissed(true)} className="text-amber-400 hover:text-amber-200 text-lg leading-none shrink-0 mt-0.5">✕</button>
        </div>
      )}

      {loading ? (
        <div className="bg-surface rounded-card border border-border-base p-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </div>
      ) : (
        <div className="bg-surface rounded-card border border-border-base">
          <div className="px-4 py-3 border-b border-border-base">
            <h2 className="font-semibold text-sm text-text-primary">Portfolio Summary</h2>
          </div>
          <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard
              label="Total Open Positions"
              value={dbPositions.length}
              sub={<span>ASX: {dbPositions.filter(p => p.market === 'ASX').length} | US: {dbPositions.filter(p => p.market === 'US').length}</span>}
            />
            <SummaryCard
              label="Total Invested"
              value={<span className="flex flex-col gap-0 text-base leading-snug font-bold">
                <span>${fmt(asxInvested, 0)} <span className="text-xs font-normal text-text-secondary">AUD</span></span>
                <span>${fmt(alpacaInvested, 0)} <span className="text-xs font-normal text-text-secondary">USD</span></span>
              </span>}
            />
            <SummaryCard
              label="Cash Available"
              value={<span className="flex flex-col gap-0 text-base leading-snug font-bold">
                <span>${fmt(asxCash, 0)} <span className="text-xs font-normal text-text-secondary">AUD</span></span>
                <span>${fmt(alpacaCash, 0)} <span className="text-xs font-normal text-text-secondary">USD</span></span>
              </span>}
            />
            <SummaryCard
              label="Total Portfolio Value"
              value={<span className="flex flex-col gap-0 text-base leading-snug font-bold">
                <span>${fmt(asxPortfolioValue, 0)} <span className="text-xs font-normal text-text-secondary">AUD</span></span>
                <span>${fmt(usPortfolioValue, 0)} <span className="text-xs font-normal text-text-secondary">USD</span></span>
              </span>}
              sub="Cash + Invested"
            />
          </div>
        </div>
      )}

      {/* Row 2 — P&L Overview */}
      <PnlOverviewTile token={token} asxUnrealisedPnl={asxUnrealisedPnl} usUnrealisedPnl={usUnrealisedPnl} />

      {/* Exchange Tabs — FIX 5: market hours with AEST */}
      <div className="bg-surface rounded-card border border-border-base">
        <div className="flex border-b border-border-base">
          {exchangeTabs.map(tab => (
            <button
              key={tab.code}
              onClick={() => setActiveTab(tab.code)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.code
                  ? 'border-border-green text-text-bright'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              <span className="flex items-baseline gap-1">
                <span className="text-xs text-text-secondary">{tab.region}</span>
                <span>{tab.name}</span>
              </span>
              <span className="text-xs bg-status-amber-soft text-status-amber-muted px-1.5 py-0.5 rounded font-medium">{tab.mode}</span>
              {(() => {
                const ms = marketStatuses[tab.code === 'US' ? 'NASDAQ' : tab.code];
                if (!ms) return null;
                return <span className={`w-1.5 h-1.5 rounded-full ${ms.is_open ? 'bg-status-green-btn animate-pulse' : 'bg-status-red'}`} />;
              })()}
            </button>
          ))}
        </div>

        {/* FIX 5: Market status with AEST display */}
        <MarketStatusRow status={activeMarketStatus} />

        <div className="p-4">
          <ExchangeTabContent
            exchange={activeTab}
            alpacaAccount={alpacaAccount}
            asxAccount={asxAccount}
            summary={summary}
            positionSignals={positionSignals}
            asxDailyPnl={asxDailyPnl}
            asxDailyPnlPct={asxDailyPnlPct}
            dbPositions={dbPositions}
            token={token}
            onHoldRemoved={loadDbPositions}
          />
        </div>
      </div>


      {/* Orders & Manual Overrides */}
      <div className="bg-surface rounded-card border border-border-base">
        <div className="px-4 py-3 border-b border-border-base">
          <h2 className="font-semibold text-sm text-text-primary">Orders & Manual Overrides</h2>
        </div>
        <OpenOrdersPanel
          openOrders={openOrders}
          token={token}
          onRefresh={refreshOrdersAndHolds}
          activeHolds={activeHolds}
          onCancelComplete={() => setTradeRefreshTrigger(prev => prev + 1)}
        />
        <ManualOverridePanel token={token} positions={allPositions} />
      </div>

      {/* Top Signals leaderboard — replaces Recent Signals (Session 6c) */}
      <TopSignalsLeaderboard token={token} onInfoClick={() => setShowSignalModal(true)} />

      {/* Signal Methodology Modal */}
      {showSignalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border-base rounded-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">How Signals Are Calculated</h3>
              <button onClick={() => setShowSignalModal(false)} className="text-text-secondary hover:text-text-bright text-xl leading-none">×</button>
            </div>
            <div className="space-y-3 text-sm text-text-primary">
              <div>
                <p className="font-semibold text-text-bright mb-1">Step 1 — Rules-Based Scoring (weights from DB)</p>
                <p>Five categories scored 0–10, weights configurable in Admin → Signals & Fundamentals.</p>
              </div>
              <div>
                <p className="font-semibold text-text-bright mb-1">Step 2 — XGBoost ML Blend (40% weight)</p>
                <p>Trained XGBoost classifier predicts BUY/HOLD/SELL based on historical patterns.</p>
              </div>
              <div>
                <p className="font-semibold text-text-bright mb-1">Step 3 — RL Adjustment (20% weight)</p>
                <p>PPO/A2C agent refines the score based on portfolio state and recent reward signals.</p>
              </div>
              <div className="bg-surface-2 rounded-lg p-3 text-xs space-y-1">
                <p className="font-semibold text-text-bright">Final Action Thresholds (configurable in Admin)</p>
                <p><span className="text-status-green font-bold">BUY</span> — score ≥ 6.5</p>
                <p><span className="text-text-secondary font-bold">HOLD</span> — score 3.5 – 6.4</p>
                <p><span className="text-status-red font-bold">SELL</span> — score ≤ 3.5</p>
              </div>
            </div>
            <button onClick={() => setShowSignalModal(false)}
              className="w-full bg-surface-2 hover:bg-surface-hover text-text-bright py-2 rounded-lg text-sm">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
